import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FireSimple, MapTrifold, Info, ArrowClockwise, VideoCamera, Star, SignIn, SignOut, UserPlus, UserCircle, ShieldCheck, Wrench, ChartLine, ShareNetwork, Check, Newspaper, Copy, BookOpen, EnvelopeSimple } from "@phosphor-icons/react";
import CountySelector from "./CountySelector";
import SavedLocationsManager from "./SavedLocationsManager";
import { useSavedLocations } from "../lib/SavedLocationsContext";
import { useAuth } from "../lib/AuthContext";
import { shareOrigin } from "../lib/shareOrigin";
import InlineShareButton from "./InlineShareButton";
import ReportsMenu from "./ReportsMenu";
import { buildSnapshotCaption } from "../lib/shareCaption";
import { fetchStats } from "../lib/api";

const LOGO_URL = "/brand-logo.png";

export default function TopNav({ onRefresh, refreshing, lastRefresh }) {
  const loc = useLocation();
  const isActive = (p) => loc.pathname === p;
  const { locations } = useSavedLocations();
  const [savedOpen, setSavedOpen] = useState(false);

  return (
    <header
      data-testid="top-nav"
      className="sticky top-0 z-[1100] border-b border-tac-border bg-tac-bg/85 backdrop-blur-xl"
    >
      <div className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-x-2 sm:gap-x-3 px-2 sm:px-3 min-[1760px]:px-6 min-h-14 py-1 sm:py-0">
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-home-link">
          <div className="relative w-10 h-10 flex items-center justify-center shrink-0">
            <img src={LOGO_URL} alt="Okanogan Highlands Firewatch emblem" className="w-10 h-10 object-contain rounded-full" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-display font-black text-[13px] sm:text-[14px] tracking-tight text-tac-text">
              OKANOGAN HIGHLANDS
            </span>
            <span className="font-display font-black text-[13px] sm:text-[14px] tracking-[0.04em] text-tac-primary -mt-0.5">
              FIREWATCH
            </span>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-0.5">
          <NavLink to="/" active={isActive("/")} icon={MapTrifold} label="Live Map" testid="nav-map" />
          <NavLink to="/incidents" active={isActive("/incidents")} icon={FireSimple} label="Incidents" testid="nav-incidents" />
          <NavLink to="/cameras" active={isActive("/cameras")} icon={VideoCamera} label="Cameras" testid="nav-cameras" />
          <NavLink to="/history" active={isActive("/history")} icon={ChartLine} label="History" testid="nav-history" />
          <NavLink to="/updates" active={isActive("/updates")} icon={EnvelopeSimple} label="Updates" testid="nav-updates" />
          <NavLink to="/guide" active={isActive("/guide")} icon={BookOpen} label="Guide" testid="nav-guide" />
          <NavLink to="/about" active={isActive("/about")} icon={Info} label="About" testid="nav-about" />
        </nav>

        <div className="flex items-center gap-1.5 min-[1760px]:gap-2">
          <button
            data-testid="open-saved-locations"
            onClick={() => setSavedOpen(true)}
            className="relative flex items-center gap-1.5 px-2.5 sm:px-3 h-9 text-xs font-bold uppercase tracking-wider border border-tac-border bg-tac-surface hover:bg-tac-surface2 hover:border-tac-primary text-tac-text rounded-sm transition-colors"
            title="Saved locations & proximity alerts"
          >
            <Star size={14} weight={locations.length > 0 ? "fill" : "regular"} className={locations.length > 0 ? "text-tac-primary" : "text-tac-dim"} />
            {locations.length > 0 && (
              <span className="text-[10px] font-mono-tac tabular text-tac-primary">{locations.length}</span>
            )}
            <span className="sr-only">Saved Locations</span>
          </button>
          <CountySelector />
          {lastRefresh && (
            <span className="hidden min-[1760px]:block text-[10px] font-mono-tac uppercase tracking-wider text-tac-faint whitespace-nowrap">
              · {lastRefresh}
            </span>
          )}
          <button
            data-testid="refresh-button"
            onClick={onRefresh}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-2.5 sm:px-3 h-9 text-xs font-semibold uppercase tracking-wider border border-tac-border bg-tac-surface hover:bg-tac-surface2 hover:border-tac-borderBright text-tac-text rounded-sm transition-colors disabled:opacity-50 active:scale-95"
          >
            <ArrowClockwise size={14} weight="bold" className={refreshing ? "animate-spin" : ""} />
            <span className="hidden min-[1760px]:inline">{refreshing ? "Syncing" : "Refresh"}</span>
          </button>
          <ShareButton />
          <ReportsMenu />
          <SnapshotButton />
          <AuthWidget />
        </div>
      </div>

      {/* Mobile sub-nav */}
      {/* Sub-nav for narrow windows: all tabs always visible, evenly compressed */}
      <nav className="xl:hidden flex items-stretch gap-0.5 sm:gap-1 px-1 sm:px-2 pb-2 border-t border-tac-border/60 pt-2">
        <SubNavLink to="/" active={isActive("/")} icon={MapTrifold} label="Map" testid="nav-map-mobile" />
        <SubNavLink to="/incidents" active={isActive("/incidents")} icon={FireSimple} label="Incidents" testid="nav-incidents-mobile" />
        <SubNavLink to="/cameras" active={isActive("/cameras")} icon={VideoCamera} label="Cameras" testid="nav-cameras-mobile" />
        <SubNavLink to="/history" active={isActive("/history")} icon={ChartLine} label="History" testid="nav-history-mobile" />
        <SubNavLink to="/updates" active={isActive("/updates")} icon={EnvelopeSimple} label="Updates" testid="nav-updates-mobile" />
        <SubNavLink to="/guide" active={isActive("/guide")} icon={BookOpen} label="Guide" testid="nav-guide-mobile" />
        <SubNavLink to="/about" active={isActive("/about")} icon={Info} label="About" testid="nav-about-mobile" />
      </nav>

      <SavedLocationsManager open={savedOpen} onClose={() => setSavedOpen(false)} />
    </header>
  );
}

function NavLink({ to, active, icon: Icon, label, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`flex items-center gap-1.5 px-2 min-[1760px]:px-3 h-9 text-xs font-semibold uppercase tracking-wider rounded-sm transition-colors whitespace-nowrap ${
        active
          ? "bg-tac-surface2 text-tac-primary border border-tac-border"
          : "text-tac-dim hover:text-tac-text hover:bg-tac-surface/60 border border-transparent"
      }`}
    >
      <Icon size={14} weight={active ? "fill" : "regular"} className="hidden min-[1536px]:inline-block" />
      {label}
    </Link>
  );
}

function SubNavLink({ to, active, icon: Icon, label, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`flex-1 min-w-0 flex items-center justify-center gap-1 sm:gap-1.5 px-0.5 sm:px-1 h-9 text-[9px] min-[440px]:text-[10px] sm:text-xs font-semibold uppercase tracking-normal min-[440px]:tracking-wide sm:tracking-wider rounded-sm transition-colors whitespace-nowrap overflow-hidden ${
        active
          ? "bg-tac-surface2 text-tac-primary border border-tac-border"
          : "text-tac-dim hover:text-tac-text hover:bg-tac-surface/60 border border-transparent"
      }`}
    >
      <Icon size={13} weight={active ? "fill" : "regular"} className="hidden min-[500px]:inline-block shrink-0" />
      {label}
    </Link>
  );
}

function AuthWidget() {
  const { user, logout } = useAuth();
  const authLoc = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, right: 0 });

  const updatePos = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) });
  }, []);

  useLayoutEffect(() => {
    if (menuOpen) updatePos();
  }, [menuOpen, updatePos]);

  useEffect(() => {
    if (!menuOpen) return;
    const onScrollOrResize = () => updatePos();
    const onClick = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen, updatePos]);

  // While session check is in flight (user === null), render nothing to avoid layout jitter
  if (user === null) {
    return <div data-testid="auth-loading" className="w-16 h-9" />;
  }

  // Not logged in
  if (user === false) {
    return (
      <div className="flex items-center gap-1.5">
        <Link
          to={`/login?next=${encodeURIComponent(authLoc.pathname + authLoc.search)}`}
          data-testid="nav-login-btn"
          className="hidden md:flex items-center gap-1.5 px-3 h-9 text-xs font-bold uppercase tracking-wider border border-tac-border bg-tac-surface hover:bg-tac-surface2 hover:border-tac-borderBright text-tac-text rounded-sm transition-colors whitespace-nowrap"
        >
          <SignIn size={13} weight="bold" />
          Log in
        </Link>
        <Link
          to="/signup"
          data-testid="nav-signup-btn"
          className="flex items-center gap-1.5 px-3 h-9 text-xs font-bold uppercase tracking-wider bg-tac-primary hover:bg-tac-primary/90 text-white rounded-sm transition-colors"
        >
          <UserPlus size={13} weight="bold" />
          Join
        </Link>
      </div>
    );
  }

  // Logged in
  const initial = (user.display_name || user.email || "?").trim().charAt(0).toUpperCase();

  const menu = menuOpen && (
    <div
      ref={menuRef}
      data-testid="auth-menu"
      style={{
        position: "fixed",
        top: pos.top,
        right: pos.right,
        width: 240,
        zIndex: 99999,
      }}
      className="bg-tac-bg border border-tac-border rounded-sm shadow-2xl overflow-hidden"
    >
      <div className="px-3 py-2.5 border-b border-tac-border">
        <div className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint">
          Signed in as
        </div>
        <div className="text-sm font-bold text-tac-text truncate mt-0.5 flex items-center gap-1.5">
          {user.display_name}
          {user.verified && (
            <ShieldCheck size={11} weight="fill" className="text-tac-primary shrink-0" />
          )}
        </div>
        <div className="text-[14px] text-tac-dim truncate">{user.email}</div>
      </div>
      <Link
        to="/profile"
        data-testid="auth-profile-link"
        onClick={() => setMenuOpen(false)}
        className="flex items-center gap-2 px-3 py-2.5 text-[15px] font-bold uppercase tracking-wider text-tac-dim hover:text-tac-text hover:bg-tac-surface transition-colors"
      >
        <UserCircle size={13} weight="bold" />
        Account
      </Link>
      {user.role === "admin" && (
        <Link
          to="/admin"
          data-testid="auth-admin-link"
          onClick={() => setMenuOpen(false)}
          className="flex items-center gap-2 px-3 py-2.5 text-[15px] font-bold uppercase tracking-wider text-tac-dim hover:text-tac-text hover:bg-tac-surface transition-colors border-t border-tac-border/50"
        >
          <Wrench size={13} weight="bold" />
          Admin console
        </Link>
      )}
      <button
        data-testid="auth-logout-btn"
        onClick={async () => {
          setMenuOpen(false);
          await logout();
          navigate(`/login?next=${encodeURIComponent(authLoc.pathname + authLoc.search)}`);
        }}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-[15px] font-bold uppercase tracking-wider text-tac-dim hover:text-tac-text hover:bg-tac-surface transition-colors border-t border-tac-border/50"
      >
        <SignOut size={13} weight="bold" />
        Log out
      </button>
    </div>
  );

  return (
    <div className="relative">
      <button
        ref={btnRef}
        data-testid="auth-user-btn"
        onClick={() => setMenuOpen((v) => !v)}
        className="flex items-center gap-2 px-2 h-9 text-xs font-semibold border border-tac-border bg-tac-surface hover:bg-tac-surface2 hover:border-tac-primary rounded-sm transition-colors"
        title={user.email}
      >
        <span className="w-6 h-6 rounded-full bg-tac-primary/15 border border-tac-primary/40 text-tac-primary flex items-center justify-center font-bold">
          {initial}
        </span>
        <span className="hidden md:block text-tac-text uppercase tracking-wider max-w-[100px] truncate">
          {user.display_name}
        </span>
      </button>
      {typeof document !== "undefined" && menu ? createPortal(menu, document.body) : null}
    </div>
  );
}
function ShareButton() {
  return (
    <InlineShareButton
      testid="topnav-share"
      url={shareOrigin()}
      title="Okanogan Highlands Firewatch"
      text="Free live wildfire dashboard for Washington — dispatch, satellite hotspots, scanner audio, evac alerts."
      caption={[
        "🔥 OKANOGAN HIGHLANDS FIREWATCH",
        "Free live wildfire dashboard for Washington — dispatch feed, satellite hotspots, scanner audio, evac alerts.",
        "",
        shareOrigin(),
      ].join("\n")}
      className="hidden sm:inline-flex"
    />
  );
}

const SNAPSHOT_REGIONS = [
  { slug: "washington", label: "Washington State" },
  { slug: "bcborder", label: "BC Border (Canada)" },
];

function SnapshotButton() {
  const [open, setOpen] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState(null);
  const ref = useRef(null);
  const loc = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const handleSelect = (slug) => {
    setOpen(false);
    navigate(`/region/${slug}`);
  };

  const handleCopy = async (slug, label) => {
    const origin = shareOrigin();
    const d = new Date();
    const day = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
    const hm = `${String(d.getHours()).padStart(2, "0")}${String(d.getMinutes()).padStart(2, "0")}`;
    const link = `${origin}/api/share/snapshot/${slug}?d=${day}&v=${hm}`;
    let active = null;
    try {
      const st = await fetchStats(slug);
      active = st?.active ?? null;
    } catch { /* caption still works without count */ }
    try {
      await navigator.clipboard.writeText(
        buildSnapshotCaption({ regionLabel: label || slug, active, url: link }),
      );
      setCopiedSlug(slug);
      setTimeout(() => { setCopiedSlug(null); setOpen(false); }, 1500);
    } catch { /* clipboard unavailable */ }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="topnav-snapshot"
        onClick={() => setOpen((v) => !v)}
        title="Copy a Daily Snapshot link — paste into Facebook for today's fire summary card"
        className="flex items-center gap-1.5 px-2.5 sm:px-3 h-9 text-xs font-semibold uppercase tracking-wider border border-tac-border bg-tac-surface hover:bg-tac-surface2 hover:border-tac-primary text-tac-text rounded-sm transition-colors active:scale-95"
      >
        <Newspaper size={14} weight="bold" />
        <span className="hidden min-[1360px]:inline min-[1760px]:hidden whitespace-nowrap">States</span>
        <span className="hidden min-[1760px]:inline whitespace-nowrap">States &amp; Provinces</span>
      </button>
      {open && (
        <div
          data-testid="snapshot-menu"
          className="absolute right-0 top-10 z-[1200] w-52 border border-tac-border bg-tac-surface shadow-xl rounded-sm py-1"
        >
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-bold text-tac-faint">
            States &amp; Provinces
          </div>
          {SNAPSHOT_REGIONS.map((r) => (
            <div key={r.slug} className="flex items-center hover:bg-tac-surface2 transition-colors">
              <button
                data-testid={`snapshot-region-${r.slug}`}
                onClick={() => handleSelect(r.slug)}
                title={`View ${r.label} dashboard`}
                className="flex-1 flex items-center justify-between px-3 py-2 text-xs font-semibold text-tac-text text-left"
              >
                <span>{r.label}</span>
                {loc.pathname === `/region/${r.slug}` && <Check size={12} weight="bold" className="text-tac-primary" />}
              </button>
              <button
                data-testid={`snapshot-copy-${r.slug}`}
                onClick={() => handleCopy(r.slug, r.label)}
                title="Copy daily snapshot share link"
                className="shrink-0 px-2.5 py-2 text-tac-dim hover:text-tac-primary transition-colors"
              >
                {copiedSlug === r.slug ? (
                  <Check size={13} weight="bold" className="text-tac-safe" />
                ) : (
                  <Copy size={13} weight="bold" />
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
